/** @jsxImportSource @emotion/react */
import {
  formContainerStyles,
  previewTitleStyles,
  previewDescriptionStyles,
} from './WorkspacesForm.styles'
import { Error, ErrorOutline } from '@mui/icons-material'
import { Box, Checkbox, FormControlLabel, FormHelperText, TextField, useTheme } from '@mui/material'
import { userAtom } from '@app/hooks/useUserProfile'
import { UserRole } from '@app/types'
import { useAtomValue } from 'jotai'
import { UseFormReturn } from 'react-hook-form'
import { LayersOrganizer } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/LayersOrganizer'
import { ModalLayer } from '@app/components/Molecules/LayersControl'
import { DrawArea } from '@app/redux/riskMap/riskMapSlice'
import { WorkspaceFilterChips } from './WorkspaceFilterChips'

interface WorkspacesFormProps {
  form: UseFormReturn<{
    name: string
    description?: string
    visibility: 'private' | 'organisation'
  }>
  layers: ModalLayer[]
  regions?: string[]
  drawAreas?: DrawArea[]
  previewConfig?: {
    titleOverride?: string | null
    descriptionOverride?: string | null
  }
}

const WorkspacesForm = ({
  form,
  layers,
  regions,
  drawAreas,
  previewConfig: preview,
}: WorkspacesFormProps) => {
  const { register, formState, watch, setValue } = form
  const { data: user } = useAtomValue(userAtom)
  const theme = useTheme()

  const visibility = watch('visibility')
  const isOrganisational = visibility === 'organisation'

  const isAdmin = user?.user_roles.includes(UserRole.ADMIN)

  const handleVisibilityChange = (checked: boolean) => {
    setValue('visibility', checked ? 'organisation' : 'private', {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  return (
    <Box css={{ display: 'flex', gap: 2, flexGrow: 1 }}>
      <Box
        component="form"
        sx={{
          minHeight: 300,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 5,
          flexGrow: 1,
        }}
      >
        <Box css={formContainerStyles}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ fontWeight: 600, mb: 2 }}>Name</Box>
            <TextField
              fullWidth
              {...register('name')}
              error={!!formState.errors.name}
              placeholder="Type a name"
              helperText={
                formState.errors.name?.message ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ErrorOutline fontSize="small" sx={{ mr: 1 }} />
                    {formState.errors.name.message}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      fontStyle: 'italic',
                      color: theme.palette.grey[500],
                      padding: '8px',
                    }}
                  >
                    Limit of 40 characters
                  </Box>
                )
              }
              slotProps={{ htmlInput: { maxLength: 40 } }}
            />
          </Box>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ fontWeight: 600, mb: 2 }}>Description</Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              {...register('description')}
              error={!!formState.errors.description}
              helperText={
                formState.errors.description?.message ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ErrorOutline fontSize="small" sx={{ mr: 1 }} />
                    {formState.errors.description.message}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      fontStyle: 'italic',
                      color: theme.palette.grey[500],
                      padding: '8px',
                    }}
                  >
                    Limit of 200 characters
                  </Box>
                )
              }
              placeholder="Type a description"
              slotProps={{ htmlInput: { maxLength: 200 }, input: { style: { padding: 0 } } }}
            />
          </Box>
          {isAdmin && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Box sx={{ fontWeight: 600 }}>Sharing</Box>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isOrganisational}
                    onChange={(e) => handleVisibilityChange(e.target.checked)}
                    disabled={!isAdmin}
                  />
                }
                slotProps={{
                  typography: {
                    sx: { lineHeight: 1.2 },
                  },
                }}
                label="Make this Workspace available to all Resilience Explorer® users in my organisation."
              />
            </Box>
          )}
        </Box>
        {formState.errors.root && (
          <FormHelperText error sx={{ display: 'flex', alignItems: 'center' }}>
            <Error fontSize="small" sx={{ mr: 1 }} />
            {formState.errors.root.message}
          </FormHelperText>
        )}
      </Box>
      <Box width="1px" bgcolor={theme.palette.grey[100] + '80'} />
      <Box
        display="flex"
        flexDirection="column"
        width={400}
        padding={3}
        gap={2}
        maxHeight="424px"
        overflow={{
          overflowY: 'auto',
        }}
      >
        <Box css={previewTitleStyles(theme, Boolean(preview?.titleOverride || watch('name')))}>
          {preview?.titleOverride ?? (watch('name') || 'No name provided')}
        </Box>
        {preview?.descriptionOverride !== null &&
          (preview?.descriptionOverride || watch('description')) && (
            <Box
              css={previewDescriptionStyles(
                theme,
                Boolean(preview?.descriptionOverride || watch('description')),
              )}
              flexGrow={1}
            >
              <span>{preview?.descriptionOverride ?? watch('description')}</span>
            </Box>
          )}
        <WorkspaceFilterChips regions={regions} drawAreas={drawAreas} />
        <Box>
          <LayersOrganizer
            isDialogPreview
            dialogProps={{
              stagedLayers: layers,
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default WorkspacesForm
